/**
 * 🩺 «چرا هیچ‌چیز ارسال نمی‌شود» — عیب‌یاب صف ارسال.
 *
 * یک پیام برای رفتن باید از چند دروازه رد شود و هر کدام می‌تواند بی‌صدا ببنددش:
 * کلید اصلی، سرویس ربات، اتصال هر حساب، سقف روزانه، فاصلهٔ حداقلی، FloodWait.
 * این کارت همهٔ دروازه‌ها را با **دلیل دقیق** نشان می‌دهد، نه «حساب آزاد نیست».
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import HealthAndSafetyRoundedIcon from '@mui/icons-material/HealthAndSafetyRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import ReplayRoundedIcon from '@mui/icons-material/Replay';
import userbotService, { UserbotQueueDiagnosis } from '../../services/userbotService';
import {
  Cell, IconAction, Insights, Kpis, Loading, Pill, Row, Rows, SectionCard, SubHead, Tag, UB,
  errMsg, fa, faDate,
} from './kit';
import type { Tone } from './kit';

const QueueDoctor: React.FC<{ onChanged?: () => void }> = ({ onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [d, setD] = useState<UserbotQueueDiagnosis | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setD(await userbotService.diagnoseQueue());
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<{ data: UserbotQueueDiagnosis; message: string }>) => {
    setBusy(true);
    try {
      const r = await fn();
      setD(r.data);
      enqueueSnackbar(r.message, { variant: 'info' });
      onChanged?.();
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const insights = useMemo(() => {
    const out: Array<{ tone: Tone; text: React.ReactNode }> = [];
    if (!d) return out;
    const healthy = d.verdict.startsWith('مانعی پیدا نشد');
    out.push({ tone: healthy ? 'good' : 'bad', text: <>{d.verdict} <span className="fac-muted">· آخرین بررسی {faDate(d.checkedAt)}</span></> });
    if (!d.bridge.ok && d.bridge.message) out.push({ tone: 'bad', text: <>سرویس ربات وصل نیست: {d.bridge.message} — تا وصل نشود هیچ پیامی به تلگرام نمی‌رود.</> });
    if (!d.liveKnown) out.push({ tone: 'warn', text: 'وضعیت زندهٔ حساب‌ها از سرویس ربات گرفته نشد؛ قضاوت از روی آخرین وضعیت ذخیره‌شده است که ممکن است کهنه باشد.' });
    const blocked = d.accounts.filter((a) => a.blockedBy);
    if (blocked.length && blocked.length === d.accounts.length && d.accounts.length > 0) {
      out.push({ tone: 'bad', text: <>هیچ‌کدام از {fa(d.accounts.length)} حساب نمی‌توانند الان پیام بفرستند؛ دلیل هر کدام در جدول پایین آمده.</> });
    }
    if (d.counts.failed24 > 0) out.push({ tone: 'warn', text: <>{fa(d.counts.failed24)} پیام در ۲۴ ساعت گذشته ناموفق بوده؛ با «برگرداندن رهاشده‌ها» دوباره به صف می‌روند.</> });
    return out;
  }, [d]);

  if (loading && !d) return <SectionCard title="عیب‌یاب صف ارسال"><Loading /></SectionCard>;
  if (!d) return null;

  return (
    <>
      <Insights items={insights} />
      <SectionCard
        icon={<HealthAndSafetyRoundedIcon />}
        title="عیب‌یاب صف ارسال"
        hint="هر دروازه‌ای که می‌تواند جلوی ارسال را بگیرد، با دلیل دقیقش"
        actions={
          <>
            <Button size="small" variant="outlined" startIcon={busy ? <CircularProgress size={14} /> : <SendIcon />} disabled={busy} onClick={() => act(() => userbotService.flushQueue())}>
              همین حالا بفرست
            </Button>
            <Button size="small" color="warning" variant="outlined" startIcon={<ReplayRoundedIcon />} disabled={busy} onClick={() => act(() => userbotService.retryFailedDrafts())}>
              برگرداندن رهاشده‌ها
            </Button>
            <IconAction title="به‌روزرسانی" onClick={load}><RefreshIcon /></IconAction>
          </>
        }
      >
        <Kpis items={[
          { label: 'در صف ارسال', value: d.counts.approved, count: true, color: d.counts.approved ? 'orange' : 'gray', help: 'تأیید شده‌اند و منتظر نوبت و فاصلهٔ طبیعی ارسال‌اند' },
          { label: 'در حال ارسال', value: d.counts.sending, count: true, color: 'blue' },
          { label: 'منتظر تأیید', value: d.counts.pending, count: true, color: d.counts.pending ? 'orange' : 'gray' },
          { label: 'منتظر سرویس', value: d.counts.waiting, count: true, color: d.counts.waiting ? 'orange' : 'gray', help: 'هوش مصنوعی در دسترس نبوده' },
          { label: 'ناموفق ۲۴ ساعت', value: d.counts.failed24, count: true, color: d.counts.failed24 ? 'red' : 'gray' },
          { label: 'سرویس ربات', raw: d.bridge.ok ? 'وصل' : 'قطع', value: null, color: d.bridge.ok ? 'green' : 'red', help: d.bridge.message || 'پل بین بک‌اند و حساب‌های تلگرام' },
        ]} />

        <SubHead hint="هر حساب یک مسیر ارسال است؛ اگر همه بسته باشند صف حرکت نمی‌کند">حساب‌ها</SubHead>
        {d.accounts.length === 0 ? (
          <p className="fac-note">هیچ حسابی ثبت نشده است.</p>
        ) : (
          <Rows cols="minmax(140px, 1fr) 150px 140px 150px minmax(200px, 1.6fr)" minWidth={820}>
            <Row head><span>حساب</span><span>وضعیت ارسال</span><span>سهمیهٔ امروز</span><span>اتصال</span><span>مانع / آخرین خطا</span></Row>
            {d.accounts.map((a) => (
              <Row key={a.id} muted={!!a.blockedBy}>
                <Cell top={a.label} sub={`فاصلهٔ حداقلی ${fa(a.minGapSeconds)} ثانیه`} />
                <span><Pill label={a.blockedBy ? 'نمی‌تواند بفرستد' : 'آمادهٔ ارسال'} color={a.blockedBy ? UB.bad : UB.ok} /></span>
                <span className="n">{fa(a.repliesToday)} <span className="fac-muted">از {fa(a.dailyCap)}</span></span>
                <span>{a.onlineLive !== null ? <Tag color={a.onlineLive ? 'success' : 'error'} label={a.onlineLive ? 'متصل به تلگرام' : 'متصل نیست'} /> : <span className="fac-muted">نامشخص</span>}</span>
                <span style={{ minWidth: 0 }}>
                  {a.blockedBy && <b className="fac-neg" style={{ display: 'block', fontWeight: 600, lineHeight: 1.8 }}>{a.blockedBy}</b>}
                  {a.lastError && <small className="fac-muted" style={{ display: 'block' }}>آخرین خطا: {a.lastError}</small>}
                  {!a.blockedBy && !a.lastError && <span className="fac-muted">—</span>}
                </span>
              </Row>
            ))}
          </Rows>
        )}

        {d.stuck.length > 0 && (
          <>
            <SubHead hint="بیشترین زمان انتظار در صف — معمولاً همان دلیل بالا">قدیمی‌ترین پیام‌های گیرکرده</SubHead>
            <Rows cols="minmax(130px, 1fr) minmax(130px, 1fr) 120px 130px minmax(180px, 1.4fr)" minWidth={780}>
              <Row head><span>فرستنده</span><span>جا</span><span>مرحله</span><span>مدت انتظار</span><span>مانع</span></Row>
              {d.stuck.map((x) => (
                <Row key={x.id}>
                  <span><b>{x.senderName || 'عضو'}</b></span>
                  <span className="fac-muted">{x.kind === 'dm' ? 'دایرکت' : x.chatTitle}</span>
                  <span><Tag label={x.stage === 'sending' ? 'در حال ارسال' : 'در صف'} /></span>
                  <span className="n">{fa(x.ageMinutes)} دقیقه{x.sendAttempts > 0 ? <small className="fac-muted" style={{ display: 'block' }}>{fa(x.sendAttempts)} تلاش ناموفق</small> : null}</span>
                  <span className="fac-warn" style={{ lineHeight: 1.8 }}>{x.blockedBy}</span>
                </Row>
              ))}
            </Rows>
          </>
        )}
      </SectionCard>
    </>
  );
};

export default QueueDoctor;
